// src/components/Admin/Campaigns/builder/ai/DraftBanner.tsx
//
// An editable AI-draft banner. It surfaces a generated draft (from any of the
// background campaign flows) in a textarea the admin can edit before choosing to
// Apply it. It NEVER auto-applies — the parent decides what to do with the value
// via onApply. Renders nothing at all when there is no draft and nothing is
// loading, so it is safe to mount unconditionally in a form.

import React from 'react';
import { GhostButton, PrimaryButton, builderStyles } from '../primitives';
import { useTokenAutocomplete } from '../useTokenAutocomplete';
import { useTokenFieldContext } from '../TokenFieldContext';
import styles from './guide.module.css';

export function DraftBanner(props: {
  label: string;
  draft: string | null;
  isLoading?: boolean;
  onApply: (value: string) => void;
  onRefresh?: () => void;
}): JSX.Element | null {
  const { label, draft, isLoading, onApply, onRefresh } = props;

  // Local editable copy, re-seeded whenever a fresh generated draft arrives.
  const [value, setValue] = React.useState<string>(draft ?? '');

  React.useEffect(() => {
    setValue(draft ?? '');
  }, [draft]);

  // The applied draft becomes authored content, so this textarea gets the same `{{`-token trigger as the
  // MUI authoring fields — variables come from the open campaign via TokenFieldContext.
  const { campaignId, uptoStepOrder } = useTokenFieldContext();
  const { ref, handleChange, popover } = useTokenAutocomplete({
    value,
    onChange: setValue,
    campaignId,
    uptoStepOrder,
  });

  // Deterministic-safe: nothing to show if AI returned null and we are idle.
  if (draft == null && !isLoading) return null;

  return (
    <div className={styles.draftBanner}>
      <div className={styles.draftBannerHead}>
        <span aria-hidden="true">✨</span>
        <span className={styles.draftBannerLabel}>{label}</span>
        {isLoading && (
          <span className={builderStyles.lead} style={{ margin: 0 }}>
            Generating…
          </span>
        )}
      </div>

      {draft != null && (
        <>
          <textarea
            ref={ref}
            className={styles.draftTextarea}
            value={value}
            onChange={handleChange}
            aria-label={label}
          />
          {popover}
          <div className={styles.draftActions}>
            <PrimaryButton onClick={() => onApply(value)} disabled={!value.trim()}>
              Apply
            </PrimaryButton>
            <GhostButton
              onClick={() => setValue(draft)}
              disabled={value === draft}
            >
              Reset to generated draft
            </GhostButton>
            {onRefresh && (
              <GhostButton onClick={onRefresh} disabled={isLoading}>
                Regenerate
              </GhostButton>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DraftBanner;
